<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu 
      :user="user" 
      :isMenuOpen="isMenuOpen" 
      @toggle-menu="toggleMenu" 
      @close-menu="closeMenu" 
      @logout="logout"
    />

    <!-- Contenido Principal - ajustado para tener margen izquierdo en escritorio -->
    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Tienda</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <!-- Tabs para productos fijos y conceptos -->
          <div class="mb-6">
            <div class="border-b border-gray-200">
              <nav class="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  @click="activeTab = 'productos'"
                  :class="[
                    activeTab === 'productos'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                    'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
                  ]"
                >
                  Productos
                </button>
                <button
                  @click="activeTab = 'conceptos'"
                  :class="[
                    activeTab === 'conceptos'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                    'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
                  ]"
                >
                  Otros Conceptos
                </button>
              </nav>
            </div>
          </div>

          <!-- Contenedor de productos fijos -->
          <div v-if="activeTab === 'productos'" class="mt-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                v-for="producto in productos"
                :key="producto.id"
                class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4 border-blue-500"
              >
                <!-- Espacio para imagen -->
                <div class="w-full h-48 bg-gray-200 mb-4 rounded-md flex items-center justify-center">
                  <span class="text-gray-500">Imagen del producto</span>
                </div>
                
                <div class="grid grid-cols-2 gap-y-2">
                  <div class="text-sm font-medium text-gray-500">Producto:</div>
                  <div class="text-sm">{{ producto.nombre }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Precio:</div>
                  <div class="text-sm">${{ producto.precio.toFixed(2) }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Descripción:</div>
                  <div class="text-sm">{{ producto.descripcion || 'N/A' }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Disponibilidad:</div>
                  <div class="text-sm">{{ producto.disponible ? 'En stock' : 'Agotado' }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Cantidad:</div>
                  <div class="text-sm">{{ producto.cantidad }}</div>
                </div>
                
                <!-- Botones para controlar la cantidad -->
                <div class="mt-4 flex justify-between">
                  <div class="flex items-center space-x-2">
                    <button 
                      @click="decrementarCantidad(producto)" 
                      class="px-2 py-1 bg-gray-200 rounded-md text-gray-700 disabled:opacity-50"
                      :disabled="producto.cantidad <= 0"
                    >
                      -
                    </button>
                    <span class="text-sm">{{ producto.cantidad }}</span>
                    <button 
                      @click="incrementarCantidad(producto)" 
                      class="px-2 py-1 bg-gray-200 rounded-md text-gray-700"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Contenedor de conceptos -->
          <div v-if="activeTab === 'conceptos'" class="mt-6">
            <div v-if="loadingConcepts" class="text-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p class="mt-2 text-gray-600">Cargando conceptos...</p>
            </div>
            
            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="concepto in conceptos"
                :key="concepto.idcharge"
                class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4 border-green-500"
              >
                <div class="grid grid-cols-2 gap-y-2">
                  <div class="text-sm font-medium text-gray-500">Concepto:</div>
                  <div class="text-sm">{{ concepto.description }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Precio:</div>
                  <div class="text-sm">${{ concepto.price.toFixed(2) }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Máximo:</div>
                  <div class="text-sm">{{ concepto.max_quantity }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Cantidad:</div>
                  <div class="text-sm">{{ concepto.cantidad }}</div>
                </div>

                <!-- Parámetros adicionales si los requiere -->
                <div v-if="concepto.requires && concepto.cantidad > 0" class="mt-4 space-y-3">
                  <!-- Selección de materias/documentos -->
                  <div v-if="concepto.params && concepto.params.length > 0">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Seleccionar {{ getParamTypeLabel(concepto.params[0]) }}:
                    </label>
                    <div class="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                      <div v-for="param in concepto.params" :key="param.idparam" class="flex items-center mb-1">
                        <input
                          type="checkbox"
                          :id="`param-${concepto.idcharge}-${param.idparam}`"
                          :value="param.idparam"
                          v-model="concepto.selectedParams"
                          :disabled="concepto.selectedParams.length >= concepto.cantidad && !concepto.selectedParams.includes(param.idparam)"
                          class="mr-2"
                        >
                        <label 
                          :for="`param-${concepto.idcharge}-${param.idparam}`"
                          class="text-sm text-gray-700"
                        >
                          {{ param.description }}
                        </label>
                      </div>
                    </div>
                    <p class="text-xs text-gray-500 mt-1">
                      Seleccionados: {{ concepto.selectedParams.length }} / {{ concepto.cantidad }}
                    </p>
                  </div>

                  <!-- Selección de periodo -->
                  <div v-if="concepto.periods && concepto.periods.length > 0">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Seleccionar periodo:
                    </label>
                    <select 
                      v-model="concepto.selectedPeriod" 
                      class="w-full border border-gray-200 rounded-md p-2 text-sm"
                    >
                      <option value="">Seleccione un periodo</option>
                      <option 
                        v-for="period in concepto.periods" 
                        :key="period.idparam" 
                        :value="period.idparam"
                      >
                        {{ period.description }}
                      </option>
                    </select>
                  </div>
                </div>
                
                <!-- Botones para controlar la cantidad -->
                <div class="mt-4 flex justify-between">
                  <div class="flex items-center space-x-2">
                    <button 
                      @click="decrementarCantidadConcepto(concepto)" 
                      class="px-2 py-1 bg-gray-200 rounded-md text-gray-700 disabled:opacity-50"
                      :disabled="concepto.cantidad <= 0"
                    >
                      -
                    </button>
                    <span class="text-sm">{{ concepto.cantidad }}</span>
                    <button 
                      @click="incrementarCantidadConcepto(concepto)" 
                      class="px-2 py-1 bg-gray-200 rounded-md text-gray-700 disabled:opacity-50"
                      :disabled="concepto.cantidad >= concepto.max_quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
            
          <!-- Resumen del carrito -->
          <div class="mt-6 bg-white shadow rounded-md p-4">
            <h2 class="text-lg font-semibold mb-2">Resumen de compra</h2>
            <div class="border-t border-gray-200 pt-2">
              <div class="flex justify-between items-center py-2">
                <span class="font-medium">Total de productos:</span>
                <span>{{ totalProductos }}</span>
              </div>
              <div class="flex justify-between items-center py-2 font-bold">
                <span>Total a pagar:</span>
                <span>${{ totalPrecio.toFixed(2) }}</span>
              </div>
              <!-- Botones de Pago -->
              <div v-if="totalProductos > 0" class="mb-4 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <button 
                  @click="showPaymentModal('bank')" 
                  class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                >
                  Pagar en línea - Total: ${{ totalPrecio.toFixed(2) }}
                </button>
                <button 
                  @click="showPaymentModal('cash')" 
                  class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                >
                  Pagar en OXXO - Total: ${{ totalPrecio.toFixed(2) }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Modal de Confirmación de Pago Estilo SweetAlert -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 swal2-container" @click="closeModal">
      <!-- Modal Container -->
      <div class="relative bg-white rounded-lg shadow-2xl max-w-lg w-full mx-auto" style="max-width: 32rem;" @click.stop>
        
        <!-- Icono de Pregunta (Estilo SweetAlert) -->
        <div class="text-center">     
          <div class="swal2-icon swal2-question swal2-icon-show" style="display: flex;"><div class="swal2-icon-content">?</div></div>
          <!-- Título Principal -->
          <h2 class="mb-2 swal2-title">Confirmar Pago</h2>

          <!-- Texto de Confirmación -->
          <p class="text-gray-600 text-lg px-4 mb-6">
            ¿Deseas realizar un pago de 
            <span class="font-bold text-gray-900">${{ totalPrecio.toFixed(2) }}</span> 
            mediante {{ modalPaymentType === 'bank' ? 'Pago en Línea' : 'Pago en OXXO' }}?
          </p>
        </div>

        <!-- Sección de Facturación (Entre texto y botones) -->
        <div class="px-6 pb-6">
          <!-- Toggle de Facturación -->
          <div class="bg-gray-50 border rounded-lg p-4 mb-4">
            <div class="flex flex-col space-y-3">
              <div class="flex items-center justify-center">
                <label class="flex items-center cursor-pointer">
                  <div class="relative">
                    <input 
                      type="checkbox" 
                      v-model="requiresInvoice" 
                      class="sr-only"
                    />
                    <div 
                      :class="[
                        'block w-12 h-6 rounded-full transition-colors duration-200',
                        requiresInvoice ? 'bg-blue-600' : 'bg-gray-300'
                      ]"
                    ></div>
                    <div 
                      :class="[
                        'absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200',
                        requiresInvoice ? 'transform translate-x-6' : ''
                      ]"
                    ></div>
                  </div>
                  <span class="ml-3 text-base font-medium text-gray-700">
                    Requiero factura
                  </span>
                </label>
              </div>
              
              <div v-if="requiresInvoice && billingDataStatus === 'loaded'" class="text-center">
                <div class="text-sm text-green-600 flex items-center justify-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Datos fiscales disponibles
                </div>
              </div>
            </div>

            <!-- Información de datos fiscales cuando se requiere factura -->
            <div v-if="requiresInvoice" class="mt-4 pt-3 border-t border-gray-200">
              <div v-if="billingDataStatus === 'loading'" class="text-center">
                <div class="flex items-center justify-center text-sm text-gray-600">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Verificando datos fiscales...
                </div>
              </div>
              
              <div v-else-if="billingDataStatus === 'no-data'" class="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
                <div class="text-center">
                  <div class="flex items-center justify-center mb-2">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                    </svg>
                    <span class="font-medium">No tienes datos fiscales registrados</span>
                  </div>
                  <button 
                    @click="goToBillingDataFromModal" 
                    class="text-blue-700 underline hover:text-blue-800 font-medium"
                  >
                    Configurar mis datos fiscales
                  </button>
                </div>
              </div>
              
              <div v-else-if="billingDataStatus === 'outdated'" class="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded text-sm">
                <div class="text-center">
                  <div class="flex items-center justify-center mb-2">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span class="font-medium">Datos fiscales desactualizados</span>
                  </div>
                  <p class="text-xs mb-2">Última actualización: {{ formatDate(billingData?.updated) }}</p>
                  <button 
                    @click="goToBillingDataFromModal" 
                    class="text-blue-700 underline hover:text-blue-800 font-medium"
                  >
                    Actualizar datos fiscales
                  </button>
                </div>
              </div>
              
              <div v-else-if="billingDataStatus === 'loaded'" class="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded text-sm">
                <div class="text-center space-y-2">
                  <p class="font-medium flex items-center justify-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Datos fiscales disponibles
                  </p>
                  <div class="text-xs space-y-1">
                    <div><strong>RFC:</strong> {{ billingData[0].rfc }}</div>
                    <div><strong>Régimen Fiscal:</strong> {{ getRegimenTexto(billingData[0].idregimen) }}</div>
                    <div><strong>Email:</strong> {{ billingData[0].email }}</div>
                  </div>
                  <button 
                    @click="goToBillingDataFromModal" 
                    class="text-blue-700 underline hover:text-blue-800 text-xs"
                  >
                    Modificar datos fiscales
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Botones Estilo SweetAlert -->
        <div class="flex justify-center space-x-3 pb-8 px-6">
          <button 
            @click="confirmPayment"
            :disabled="requiresInvoice && billingDataStatus !== 'loaded'"
            :class="[
              'px-6 py-2 text-white font-medium rounded-md transition-colors text-base',
              (requiresInvoice && billingDataStatus !== 'loaded') 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-500 hover:bg-green-600'
            ]"
          >
            Sí, proceder
          </button>
          <button 
            @click="closeModal"
            class="px-6 py-2 bg-red-500 text-white font-medium rounded-md hover:bg-red-600 transition-colors text-base"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { getBillingData, getConcepts } from "@/services/api";

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      studentId: null,
      activeTab: 'productos',
      loadingConcepts: false,
      conceptos: [],
      productos: [
        {
          id: 1,
          nombre: 'Constancia de estudios',
          precio: 300,
          descripcion: 'Documento oficial que acredita tus estudios',
          disponible: true,
          cantidad: 0
        },
        {
          id: 2,
          nombre: 'Constancia de no adeudo',
          precio: 150,
          descripcion: 'Documento que certifica que no tienes pagos pendientes',
          disponible: true,
          cantidad: 0
        },
        {
          id: 3,
          nombre: 'Playera',
          precio: 300,
          descripcion: 'Playera oficial con el logo de la institución',
          disponible: true,
          cantidad: 0
        },
        {
          id: 4,
          nombre: 'Examen',
          precio: 180,
          descripcion: 'Derecho a examen extraordinario',
          disponible: true,
          cantidad: 0
        },
        {
          id: 5,
          nombre: 'Credencial',
          precio: 120,
          descripcion: 'Reposición de credencial de estudiante',
          disponible: true,
          cantidad: 0
        }
      ],
      carrito: [],
      requiresInvoice: false,
      billingData: null,
      billingDataStatus: 'loading',
      showModal: false,
      modalPaymentType: null,
      catalogoRegimenes: {
        601: "601 - General de Ley Personas Morales",
        603: "603 - Personas Morales con Fines no Lucrativos",
        605: "605 - Sueldos y Salarios e Ingresos Asimilados a Salarios",
        606: "606 - Arrendamiento",
        607: "607 - Régimen de Enajenación o Adquisición de Bienes",
        608: "608 - Demás ingresos",
        610: "610 - Residentes en el Extranjero sin Establecimiento Permanente en México",
        611: "611 - Ingresos por Dividendos (socios y accionistas)",
        612: "612 - Personas Físicas con Actividades Empresariales y Profesionales",
        614: "614 - Ingresos por intereses",
        615: "615 - Régimen de los ingresos por obtención de premios",
        616: "616 - Sin obligaciones fiscales",
        620: "620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos",
        621: "621 - Incorporación Fiscal",
        622: "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
        623: "623 - Opcional para Grupos de Sociedades",
        624: "624 - Coordinados",
        625: "625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas",
        626: "626 - Régimen Simplificado de Confianza"
      }
    };
  },
  computed: {
    totalProductos() {
      const productosCount = this.productos.reduce((total, producto) => total + producto.cantidad, 0);
      const conceptosCount = this.conceptos.reduce((total, concepto) => total + concepto.cantidad, 0);
      return productosCount + conceptosCount;
    },
    totalPrecio() {
      const productosTotal = this.productos.reduce((total, producto) => total + (producto.cantidad * producto.precio), 0);
      const conceptosTotal = this.conceptos.reduce((total, concepto) => total + (concepto.cantidad * concepto.price), 0);
      return productosTotal + conceptosTotal;
    }
  },
  watch: {
    requiresInvoice(newValue) {
      if (newValue) {
        this.checkBillingData();
      }
    },
    activeTab(newValue) {
      if (newValue === 'conceptos' && this.conceptos.length === 0) {
        this.loadConcepts();
      }
    }
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    // Cargar conceptos desde la API
    async loadConcepts() {
      if (!this.studentId) return;
      
      this.loadingConcepts = true;
      try {
        const response = await getConcepts({
          idstudent: this.studentId,
          idoption: 1,
          idconcept: 0
        });
        
        this.conceptos = response.data.map(concepto => ({
          ...concepto,
          cantidad: 0,
          selectedParams: [],
          selectedPeriod: concepto.idperiod,
          params: [],
          periods: []
        }));
      } catch (error) {
        console.error('Error cargando conceptos:', error);
      } finally {
        this.loadingConcepts = false;
      }
    },

    // Cargar parámetros adicionales para un concepto
    async loadConceptParams(concepto) {
      try {
        const response = await getConcepts({
          idstudent: this.studentId,
          idoption: 2,
          idconcept: concepto.idcharge
        });
        
        // Separar entre materias/documentos y periodos
        const params = response.data.filter(param => param.type !== 'period');
        const periods = response.data.filter(param => param.type === 'period');
        
        concepto.params = params;
        concepto.periods = periods;
      } catch (error) {
        console.error('Error cargando parámetros del concepto:', error);
      }
    },

    // Obtener etiqueta para el tipo de parámetro
    getParamTypeLabel(param) {
      if (param.description.toLowerCase().includes('materia')) return 'materias';
      if (param.description.toLowerCase().includes('documento')) return 'documentos';
      return 'opciones';
    },

    // Método para formatear fechas en formato yyyy-mm-dd
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    },

    // Verificar datos de facturación del estudiante
    async checkBillingData() {
      this.billingDataStatus = 'loading';
      
      try {
        const response = await getBillingData(this.studentId);
        this.billingData = response.data;
        
        // Verificar si los datos están actualizados (menos de 6 meses)
        const updatedDate = new Date(this.billingData.updated);
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        
        if (updatedDate < sixMonthsAgo) {
          this.billingDataStatus = 'outdated';
        } else {
          this.billingDataStatus = 'loaded';
        }
        
      } catch (error) {
        if (error.response?.status === 404) {
          this.billingDataStatus = 'no-data';
        } else {
          console.error('Error al verificar datos de facturación:', error);
          this.billingDataStatus = 'no-data';
        }
      }
    },

    // Mostrar modal de confirmación de pago
    showPaymentModal(type) {
      this.modalPaymentType = type;
      this.showModal = true;
    },

    // Cerrar modal
    closeModal() {
      this.showModal = false;
      this.requiresInvoice = false;
      this.modalPaymentType = null;
      this.billingDataStatus = 'loading';
      this.billingData = null;
    },

    // Redirigir a datos fiscales desde el modal
    goToBillingDataFromModal() {
      this.closeModal();
      this.$router.push('/mis-datos-facturacion');
    },

    // Confirmar el pago desde el modal
    confirmPayment() {
      this.showModal = false;
      this.initiatePayment(this.modalPaymentType);
    },

    // Métodos para productos fijos
    incrementarCantidad(producto) {
      producto.cantidad++;
    },
    decrementarCantidad(producto) {
      if (producto.cantidad > 0) {
        producto.cantidad--;
      }
    },

    // Métodos para conceptos
    async incrementarCantidadConcepto(concepto) {
      if (concepto.cantidad < concepto.max_quantity) {
        concepto.cantidad++;
        
        // Si requiere parámetros y no los hemos cargado, cargarlos
        if (concepto.requires && concepto.params.length === 0) {
          await this.loadConceptParams(concepto);
        }
      }
    },
    decrementarCantidadConcepto(concepto) {
      if (concepto.cantidad > 0) {
        concepto.cantidad--;
        
        // Ajustar selecciones si es necesario
        if (concepto.selectedParams.length > concepto.cantidad) {
          concepto.selectedParams = concepto.selectedParams.slice(0, concepto.cantidad);
        }
      }
    },

    agregarAlCarrito(producto) {
      if (producto.cantidad > 0) {
        alert(`Agregado al carrito: ${producto.cantidad} ${producto.nombre}`);
      }
    },

    // Validar que un concepto esté correctamente configurado
    validateConcepto(concepto) {
      if (concepto.cantidad === 0) return false;
      
      // Si requiere parámetros de materias/documentos, debe seleccionar la cantidad correcta
      if (concepto.requires && concepto.params.length > 0) {
        if (concepto.selectedParams.length !== concepto.cantidad) {
          return false;
        }
      }
      
      // Si requiere periodo y es diferente al actual, debe seleccionarlo
      if (concepto.requires && concepto.periods.length > 0) {
        if (!concepto.selectedPeriod) {
          return false;
        }
      }
      
      return true;
    },

    async initiatePayment(type) {
      if (this.totalProductos === 0) {
        await this.$alert(
          'Selección requerida',
          'Por favor, seleccione al menos un producto para pagar.',
          'warning'
        );
        return;
      }

      // Validar conceptos antes del pago
      const invalidConcepts = this.conceptos.filter(concepto => 
        concepto.cantidad > 0 && !this.validateConcepto(concepto)
      );
      
      if (invalidConcepts.length > 0) {
        const conceptNames = invalidConcepts.map(c => c.description).join(', ');
        await this.$alert(
          'Configuración incompleta',
          `Los siguientes conceptos requieren configuración adicional: ${conceptNames}`,
          'warning'
        );
        return;
      }

      // Crear orden de pago con el formato correcto
      const concepts = [];
      
      // Agregar productos fijos
      this.productos.forEach(producto => {
        if (producto.cantidad > 0) {
          concepts.push({
            idproduct: producto.id,
            description: producto.nombre,
            quantity: producto.cantidad,
            unit_price: producto.precio,
            charge: producto.cantidad * producto.precio
          });
        }
      });
      
      // Agregar conceptos dinámicos
      this.conceptos.forEach(concepto => {
        if (concepto.cantidad > 0) {
          const conceptData = {
            idpayment: concepto.selectedPeriod !== concepto.idperiod ? 0 : concepto.idpayment,
            idperiod: concepto.selectedPeriod || concepto.idperiod,
            idcharge: concepto.idcharge,
            description: concepto.selectedPeriod !== concepto.idperiod ? 
              concepto.long_description : concepto.description,
            charge: concepto.cantidad * concepto.price
          };
          
          // Agregar parámetros extra si los hay
          if (concepto.selectedParams.length > 0) {
            conceptData.extra_params = concepto.selectedParams.join(',');
          }
          
          concepts.push(conceptData);
        }
      });

      const order = {
        idstudent: this.studentId,
        concepts: concepts,
        username: '',
        invoice: this.requiresInvoice
      };

      this.processPayment(type, order);
    },

    async processPayment(type, order) {
      try {
        const PAYMENTS_URL = process.env.VUE_APP_PAYMENTS_URL;

        const response = await axios.post(`${PAYMENTS_URL}/payments/api/${type}`, order, {
          withCredentials: false,
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        });

        if (response.data.url) {
          await this.$paymentLink(response.data.url, {
            showShare: true,
            showCopy: true,
            showOpen: true,
            enableShare: true,
            enableCopy: true,
            enableOpen: true,
            shareOptions: {
              email: true,
              whatsapp: true,
              sms: true,
              contacts: true
            }
          });

          // Resetear cantidades después del pago exitoso
          this.productos.forEach(producto => {
            producto.cantidad = 0;
          });
          
          this.conceptos.forEach(concepto => {
            concepto.cantidad = 0;
            concepto.selectedParams = [];
            concepto.selectedPeriod = concepto.idperiod;
          });
          
          this.requiresInvoice = false;
        } else {
          await this.$alert(
            'Error en el pago',
            'No se pudo generar la liga de pago.',
            'error'
          );
        }
      } catch (error) {
        console.error('Error al procesar el pago:', error);
        
        let errorMessage = 'Error desconocido al procesar el pago.';
        
        if (error.response) {
          console.error('Datos del error:', error.response.data);
          console.error('Código de estado:', error.response.status);
          errorMessage = `Error ${error.response.status}: ${error.response.data.message || 'Error del servidor'}`;
        } else if (error.request) {
          console.error('Sin respuesta del servidor:', error.request);
          errorMessage = 'No se recibió respuesta del servidor. Verifique su conexión.';
        } else {
          console.error('Error de configuración:', error.message);
          errorMessage = 'Error al configurar la solicitud.';
        }

        await this.$alert('Error de pago', errorMessage, 'error');
      }
    },

    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];
        } else {
          console.error("No se pudo extraer el ID de estudiante del email.");
        }
      } catch (error) {
        console.error('Error obteniendo usuario', error);
        this.$router.push('/login');
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    async logout() {
      try {
        await axios.get('/sanctum/csrf-cookie');
        await new Promise(resolve => setTimeout(resolve, 100));
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    },
    getRegimenTexto(id) {
      return this.catalogoRegimenes[id] || "Régimen no encontrado";
    }
  }
};
</script>